import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { capitalizeFirstLetter, dateFormatted } from "modules/functions";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import AdPlansSelection from "./AdPlansSelection";
import ButtonSecondarySmallDanger from "components/buttons/ButtonSecondarySmallDanger";
import ButtonWhite from "components/buttons/ButtonWhite";
import ButtonPrimaryDanger from "components/buttons/ButtonPrimaryDanger";
import ButtonWhiteSmall from "components/buttons/ButtonWhiteSmall";
import ButtonPrimarySmallDanger from "components/buttons/ButtonPrimarySmallDanger";
import RollingBox from "components/other/RollingBox";

import adPlanIcon from "assets/img/icons/icons8-web-advertising.svg";
import bronzeCheckmarkIcon from "assets/img/icons/checkmark-bronze.svg";
import silverCheckmarkIcon from "assets/img/icons/checkmark-silver.svg";
import goldCheckmarkIcon from "assets/img/icons/checkmark-gold.svg";
import featureTickIcon from "assets/img/icons/icons8-feature-tick.svg";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import creditcardIcon from "assets/img/icons/icons8-magnetic-card.svg";
import visaIcon from "assets/img/icons/icons8-visa.svg";
import mastercardIcon from "assets/img/icons/icons8-mastercard.svg";
import amexIcon from "assets/img/icons/icons8-american-express.svg";
import discoverIcon from "assets/img/icons/icons8-discover-card.svg";
import stripeIcon from "assets/img/icons/icons8-stripe.svg";

const ManageAdPlan = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 6px;

  transform: translateY(4.5px);

  width: 26px;
  min-width: 26px;
`;

const Text = styled.div`
  line-height: 130%;
`;

const Subscription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  flex-wrap: wrap;
`;

const DataIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DataIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const DataText = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: ${(props) => (props.status === "canceled" ? css.DARK_BLUE : "#fff")};
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return css.BRAND_GREEN;
      case "expired":
        return css.NEGATIVE_RED;
      case "canceled":
        return css.TEXT_GREY;
      default:
        return css.TEXT_GREY;
    }
  }};

  transform: translateY(1px);

  display: flex;
  justify-content: center;
  align-items: center;
`;

const HistoryButtonWrap = styled.div`
  display: flex;
`;

const PaymentMethodBox = styled.div`
  padding: ${css.BOX_PADDING_TERTIARY};
  width: 100%;
  min-width: 240px;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  justify-content: space-between;
`;

const PaymentMethodContent = styled.div`
  display: flex;
  gap: 5px;
`;

const PaymentMethodIcon = styled.div`
  width: 38px;
  min-width: 38px;

  transform: translateY(-5px);
`;

const PaymentMethodData = styled.div`
  font-size: 15px;

  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PaymentMethodCardInfo = styled.div``;

const PaymentMethodExpiry = styled.div`
  color: ${css.TEXT_GREY};
`;

const PaymentMethodEditLink = styled.div`
  font-size: 15px;
  color: ${css.LIGHT_BLUE};

  &:hover {
    cursor: pointer;
  }
`;

const AdPlans = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const ChangePlanTitle = styled.div`
  line-height: 130%;
  font-size: 19px;
  font-weight: 500;
`;

const AdPlansInfo = styled.div`
  padding: 10px 0;
  max-width: 450px;

  text-align: center;

  line-height: 130%;

  align-self: center;
`;

const AdPlansInfoLink = styled(Link)`
  display: inline-block;

  text-decoration: none;
  color: ${css.LIGHT_BLUE};
`;

const CancelButtonBox = styled.div`
  padding-bottom: 10px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const ConfirmCancelForm = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: 350px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 9px;
  }
`;

const SubscriptionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const ChangePlan = styled.div`
  margin: 15px 0;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

export default ({
  data,
  onPlanSelect,
  toggleCancelForm,
  confirmCancelForm,
  cancelBlock,
  onCancelSubsription,
  onEditPaymentMethodClick,
  toggleChangePlanForm,
  changePlanForm,
}) => {
  const navigate = useNavigate();

  const plan = data.adPlanBillingData.adPlan;
  //console.log({ plan });

  let planIcon;
  let planName;
  let product;

  switch (plan) {
    case 1:
      planIcon = bronzeCheckmarkIcon;
      planName = "Bronze";
      product = "bronze_ad_plan";
      break;
    case 2:
      planIcon = silverCheckmarkIcon;
      planName = "Silver";
      product = "silver_ad_plan";
      break;
    case 3:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 20";
      product = "gold_20_ad_plan";
      break;
    case 4:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 40";
      product = "gold_40_ad_plan";
      break;
    case 5:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 60";
      product = "gold_60_ad_plan";
      break;
    case 6:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 80";
      product = "gold_80_ad_plan";
      break;
    case 7:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 100";
      product = "gold_100_ad_plan";
      break;
  }

  const planPrice = data.adPlanBillingData.prices.find(
    (price) => price.product === product
  );

  let bronzePrice = 0;
  let silverPrice = 0;
  let goldPrice = 0;

  for (const price of data.adPlanBillingData.prices) {
    if (price.product === "bronze_ad_plan") bronzePrice = price.price;
    if (price.product === "silver_ad_plan") silverPrice = price.price;
    if (price.product === "gold_20_ad_plan") goldPrice = price.price;
  }

  const {
    status,
    expires,
    stripe_card_brand,
    stripe_card_ending,
    stripe_card_exp_month,
    stripe_card_exp_year,
  } = data.adPlanBillingData.subscription;

  let statusText;

  switch (status) {
    case "active":
      statusText = "Active";
      break;
    case "expired":
      statusText = "Expired";
      break;
    case "canceled":
      statusText = "Canceled";
      break;
  }

  let cardIcon = creditcardIcon;
  let methodMainDescription = `${capitalizeFirstLetter(
    stripe_card_brand
  )} ending ${stripe_card_ending}`;
  let methodSubDescription = `Expires ${stripe_card_exp_month}/${stripe_card_exp_year}`;

  switch (stripe_card_brand) {
    case "visa":
      cardIcon = visaIcon;
      break;
    case "mastercard":
      cardIcon = mastercardIcon;
      break;
    case "amex":
      cardIcon = amexIcon;
      break;
    case "discover":
      cardIcon = discoverIcon;
    case "link":
      cardIcon = stripeIcon;
      methodMainDescription = "Link by Stripe";
      methodSubDescription = "Card managed by Stripe";
      break;
  }

  return (
    <ManageAdPlan>
      <TopContent>
        <Heading>
          <BoxTitle>
            <TitleIcon>
              <img src={adPlanIcon} style={{ width: "100%" }} />
            </TitleIcon>
            Your Advertising Plan Subscription
          </BoxTitle>
          <Text>
            Modify your subscription, change the payment method, or cancel
            recurring payments.
          </Text>
        </Heading>
        <Subscription>
          <Data>
            <DataTitle>Subscription</DataTitle>
            <DataRow>
              <DataIconBox>
                <DataIcon>
                  <img src={planIcon} style={{ width: "100%" }} />
                </DataIcon>
              </DataIconBox>
              <DataText>
                {`${planName} Ad Plan $${planPrice.price / 100} USD/month`}
              </DataText>
              <StatusBadge status={status}>{statusText}</StatusBadge>
            </DataRow>
          </Data>
          <HistoryButtonWrap>
            {!changePlanForm && (
              <ButtonPrimarySmall onClick={toggleChangePlanForm}>
                Change Plan
              </ButtonPrimarySmall>
            )}
            {changePlanForm && (
              <ButtonWhiteSmall onClick={toggleChangePlanForm}>
                Close
              </ButtonWhiteSmall>
            )}
          </HistoryButtonWrap>
        </Subscription>
      </TopContent>
      <RollingBox>
        {changePlanForm && (
          <ChangePlan>
            <ChangePlanTitle>Change Ad Plan</ChangePlanTitle>
            <AdPlansSelection
              prices={data.adPlanBillingData.prices}
              onPlanSelect={onPlanSelect}
              activePlan={plan}
            ></AdPlansSelection>
            <AdPlansInfo>
              For a detailed description of the features, please visit{" "}
              <AdPlansInfoLink
                to="https://help.scalelup.com/en/articles/10962832-detailed-overview-of-scalelup-advertising-plan-features"
                target="_blank"
              >
                this article
              </AdPlansInfoLink>
              .
            </AdPlansInfo>
          </ChangePlan>
        )}
      </RollingBox>
      <SubscriptionDetails>
        <Data>
          {status === "active" && <DataTitle>Renewal</DataTitle>}
          {status === "canceled" && <DataTitle>Expires</DataTitle>}
          {status === "expired" && <DataTitle>Expired</DataTitle>}
          <DataRow>
            <DataText>{dateFormatted(new Date(expires), true, true)}</DataText>
          </DataRow>
        </Data>
        {status !== "canceled" && (
          <Data>
            <DataTitle>Payment Menthod</DataTitle>
            <DataRow>
              <DataText>
                <PaymentMethodBox>
                  <PaymentMethodContent>
                    <PaymentMethodIcon>
                      <img src={cardIcon} style={{ width: "100%" }} />
                    </PaymentMethodIcon>
                    <PaymentMethodData>
                      <PaymentMethodCardInfo>
                        {methodMainDescription}
                      </PaymentMethodCardInfo>
                      <PaymentMethodExpiry>
                        {methodSubDescription}
                      </PaymentMethodExpiry>
                    </PaymentMethodData>
                  </PaymentMethodContent>
                  <PaymentMethodEditLink onClick={onEditPaymentMethodClick}>
                    Edit
                  </PaymentMethodEditLink>
                </PaymentMethodBox>
              </DataText>
            </DataRow>
          </Data>
        )}
        <HistoryButtonWrap>
          <ButtonPrimarySmall onClick={() => navigate("/wallet")}>
            Billing History
          </ButtonPrimarySmall>
        </HistoryButtonWrap>
        {status !== "canceled" && (
          <Data>
            <DataTitle>Cancel Subscription</DataTitle>
            <DataRow>
              <CancelButtonBox>
                <DataText>
                  Your plan will run until the end of the prepaid period.
                </DataText>
                {!confirmCancelForm && (
                  <ButtonSecondarySmallDanger onClick={toggleCancelForm}>
                    Cancel Subscription
                  </ButtonSecondarySmallDanger>
                )}
                {confirmCancelForm && (
                  <ConfirmCancelForm>
                    <ButtonWhiteSmall onClick={toggleCancelForm}>
                      Keep Subscription
                    </ButtonWhiteSmall>
                    <ButtonPrimarySmallDanger
                      disabled={cancelBlock}
                      onClick={onCancelSubsription}
                    >
                      Cancel Subscription
                    </ButtonPrimarySmallDanger>
                  </ConfirmCancelForm>
                )}
              </CancelButtonBox>
            </DataRow>
          </Data>
        )}
      </SubscriptionDetails>
    </ManageAdPlan>
  );
};
